<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api } from "@/api/client";
import { describeError } from "@/utils/errors";
import type { OperatorAdminView, OperatorRole } from "@/api/types";
import { useAuthStore } from "@/stores/auth";

const { t } = useI18n();
const auth = useAuthStore();

const items = ref<OperatorAdminView[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);
const search = ref("");

const newEmail = ref("");
const newPassword = ref("");
const newRole = ref<OperatorRole>("operator");
const newOrg = ref("");
const formError = ref<string | null>(null);
const submitting = ref(false);

const assignableRoles = computed<OperatorRole[]>(() => {
  const base: OperatorRole[] = ["admin", "operator", "viewer"];
  return auth.hasRole(["owner"]) ? ["owner", ...base] : base;
});

async function reload() {
  loading.value = true;
  listError.value = null;
  try {
    const resp = await api.admin.operators.list();
    items.value = resp.operators;
  } catch (err) {
    listError.value = errorMessage(err);
  } finally {
    loading.value = false;
  }
}

async function submit() {
  formError.value = null;
  submitting.value = true;
  try {
    await api.admin.operators.create({
      email: newEmail.value.trim(),
      password: newPassword.value,
      role: newRole.value,
      organization_id: auth.hasRole(["owner"]) && newOrg.value ? newOrg.value : undefined,
    });
    newEmail.value = "";
    newPassword.value = "";
    newRole.value = "operator";
    newOrg.value = "";
    await reload();
  } catch (err) {
    formError.value = errorMessage(err);
  } finally {
    submitting.value = false;
  }
}

async function toggleStatus(op: OperatorAdminView) {
  try {
    await api.admin.operators.update(op.id, {
      status: op.status === "enabled" ? "disabled" : "enabled",
    });
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function setTerminalLogin(op: OperatorAdminView, ev: Event) {
  const input = ev.target as HTMLInputElement;
  const value = input.value.trim();
  if (value === (op.terminal_login ?? "")) return;
  try {
    await api.admin.operators.update(op.id, { terminal_login: value });
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
    input.value = op.terminal_login ?? "";
  }
}

async function setApprovalScopes(op: OperatorAdminView, ev: Event) {
  const input = ev.target as HTMLInputElement;
  const scopes = input.value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (scopes.join(",") === (op.approval_scopes ?? []).join(",")) return;
  try {
    await api.admin.operators.update(op.id, { approval_scopes: scopes });
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
    input.value = (op.approval_scopes ?? []).join(", ");
  }
}

async function remove(op: OperatorAdminView) {
  if (!window.confirm(t("admin.confirm.delete_operator", { email: op.email }))) return;
  try {
    await api.admin.operators.delete(op.id);
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

function errorMessage(err: unknown): string {
  return describeError(err, t);
}

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return items.value;
  return items.value.filter(
    (o) => o.email.toLowerCase().includes(q) || o.role.toLowerCase().includes(q),
  );
});

onMounted(reload);
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>{{ t("admin.nav.operators") }}</h1>
      <input v-model="search" type="search" :placeholder="t('admin.search')" />
    </div>

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("admin.operators.create_title") }}</h2>
      <form class="admin-form" @submit.prevent="submit">
        <label>
          <span>{{ t("login.email") }}</span>
          <input v-model="newEmail" type="email" required :disabled="submitting" />
        </label>
        <label>
          <span>{{ t("login.password") }}</span>
          <input v-model="newPassword" type="password" required :disabled="submitting" />
        </label>
        <label>
          <span>{{ t("admin.operators.role") }}</span>
          <select v-model="newRole" :disabled="submitting">
            <option v-for="r in assignableRoles" :key="r" :value="r">{{ t(`role.${r}`) }}</option>
          </select>
        </label>
        <label v-if="auth.hasRole(['owner'])">
          <span>{{ t("admin.operators.organization") }}</span>
          <input
            v-model="newOrg"
            :placeholder="t('admin.operators.org_placeholder')"
            :disabled="submitting"
          />
        </label>
        <button type="submit" class="admin-form__submit" :disabled="submitting">
          {{ submitting ? t("admin.submitting") : t("admin.create") }}
        </button>
      </form>
      <p v-if="formError" class="admin-error" style="margin-top: 0.5rem">{{ formError }}</p>
    </section>

    <section class="admin-card">
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="filtered.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("login.email") }}</th>
            <th>{{ t("admin.operators.role") }}</th>
            <th>{{ t("admin.status") }}</th>
            <th>{{ t("admin.operators.terminal_login") }}</th>
            <th>{{ t("admin.operators.approval_scopes") }}</th>
            <th>{{ t("admin.operators.organization") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="op in filtered" :key="op.id">
            <td :data-label="t('login.email')">{{ op.email }}</td>
            <td :data-label="t('admin.operators.role')">
              <span class="admin-badge" data-tone="info">{{ t(`role.${op.role}`) }}</span>
            </td>
            <td :data-label="t('admin.status')">
              <span class="admin-badge" :data-tone="op.status === 'enabled' ? 'ok' : 'bad'">{{
                t(`admin.status_${op.status}`)
              }}</span>
            </td>
            <td :data-label="t('admin.operators.terminal_login')">
              <input
                class="operators__login"
                :value="op.terminal_login ?? ''"
                :placeholder="t('admin.operators.terminal_login_ph')"
                :title="t('admin.operators.terminal_login_hint')"
                :data-test="`op-login-${op.id}`"
                @change="setTerminalLogin(op, $event)"
              />
            </td>
            <td :data-label="t('admin.operators.approval_scopes')">
              <input
                class="operators__scopes"
                :value="(op.approval_scopes ?? []).join(', ')"
                :placeholder="t('admin.operators.approval_scopes_ph')"
                :title="t('admin.operators.approval_scopes_hint')"
                :data-test="`op-scopes-${op.id}`"
                @change="setApprovalScopes(op, $event)"
              />
            </td>
            <td class="admin-table__id" :data-label="t('admin.operators.organization')">
              {{ op.organization_id.slice(0, 8) }}
            </td>
            <td class="admin-table__actions">
              <button type="button" @click="toggleStatus(op)">
                {{
                  op.status === "enabled"
                    ? t("admin.operators.disable")
                    : t("admin.operators.enable")
                }}
              </button>
              <button type="button" @click="remove(op)">{{ t("admin.delete") }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<style scoped>
.operators__login {
  width: 8.5rem;
  max-width: 100%;
  font-size: 0.85rem;
}
.operators__scopes {
  width: 11rem;
  max-width: 100%;
  font-size: 0.85rem;
}
</style>
